import React, { useState, useEffect } from 'react';
import { jobInquiriesApi, jobPositionTypesApi, staffApi, rolesApi } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlinePlus,
  HiOutlineSearch,
  HiOutlineFilter,
  HiOutlineEye,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineDocumentDownload,
  HiOutlineDocumentText,
  HiOutlineUserAdd,
  HiX
} from 'react-icons/hi';
import JobInquiryForm from '../../components/Forms/JobInquiryForm';
import { formatDateTimeShortIST } from '../../utils/timezoneDisplay';

const JobInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    position_type: '',
    date_from: '',
    date_to: '',
    page: 1
  });
  const [pagination, setPagination] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [inquiryToDelete, setInquiryToDelete] = useState(null);
  const [positionTypes, setPositionTypes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertInquiry, setConvertInquiry] = useState(null);
  const [converting, setConverting] = useState(false);
  const [convertForm, setConvertForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    role_id: '',
    password: '',
    date_of_birth: '',
    basic_salary: '',
    allowances: '',
    deductions: ''
  });

  useEffect(() => {
    fetchInquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.position_type, filters.date_from, filters.date_to, filters.page]);

  useEffect(() => {
    const loadPositionTypes = async () => {
      try {
        const res = await jobPositionTypesApi.getActive();
        setPositionTypes(res.data?.data || []);
      } catch {
        setPositionTypes([]);
      }
    };
    loadPositionTypes();
  }, []);

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const res = await rolesApi.getList();
        const list = res.data?.data || [];
        setRoles(list.filter((r) => (r.name || '').toLowerCase() !== 'admin'));
      } catch {
        setRoles([]);
      }
    };
    loadRoles();
  }, []);

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const params = {
        ...(filters.search ? { search: filters.search } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.position_type ? { position_type: filters.position_type } : {}),
        ...(filters.date_from ? { date_from: filters.date_from } : {}),
        ...(filters.date_to ? { date_to: filters.date_to } : {}),
        page: filters.page,
        limit: 20
      };
      const response = await jobInquiriesApi.getAll(params);
      setInquiries(response.data.data.inquiries || []);
      setPagination(response.data.data.pagination || {});
    } catch (error) {
      toast.error('Failed to fetch job inquiries');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters({ ...filters, page: 1 });
    fetchInquiries();
  };

  const handleFilterChange = (name, value) => {
    setFilters({ ...filters, [name]: value, page: 1 });
  };

  const handleCreate = () => {
    setSelectedInquiry(null);
    setShowForm(true);
  };

  const handleEdit = (inquiry) => {
    setSelectedInquiry(inquiry);
    setShowForm(true);
  };

  const handleDelete = (inquiry) => {
    setInquiryToDelete(inquiry);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!inquiryToDelete) return;

    try {
      await jobInquiriesApi.delete(inquiryToDelete.id);
      toast.success('Job inquiry deleted successfully');
      setShowDeleteConfirm(false);
      setInquiryToDelete(null);
      fetchInquiries();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete job inquiry');
    }
  };

  const handleStatusChange = async (inquiryId, newStatus) => {
    try {
      await jobInquiriesApi.updateStatus(inquiryId, newStatus);
      toast.success('Status updated successfully');
      fetchInquiries();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  const handleDownloadResume = async (inquiry) => {
    try {
      const response = await jobInquiriesApi.downloadResume(inquiry.id);
      if (response.data.success && response.data.data.url) {
        window.open(response.data.data.url, '_blank');
      }
    } catch (error) {
      toast.error('Failed to download resume');
    }
  };

  const openConvertModal = (inquiry) => {
    setConvertInquiry(inquiry);
    setConvertForm({
      full_name: inquiry.full_name || '',
      email: inquiry.email || '',
      phone: inquiry.contact_number || '',
      role_id: '',
      password: '',
      date_of_birth: '',
      basic_salary: '',
      allowances: '',
      deductions: ''
    });
    setShowConvertModal(true);
  };

  const closeConvertModal = () => {
    setShowConvertModal(false);
    setConvertInquiry(null);
    setConverting(false);
  };

  const handleConvertToStaff = async (e) => {
    e.preventDefault();
    if (!convertInquiry) return;
    if (!convertForm.full_name.trim() || !convertForm.role_id || !convertForm.password.trim()) {
      toast.error('Name, Role and Password are required');
      return;
    }

    setConverting(true);
    try {
      const currentClinicId = Number(localStorage.getItem('currentClinicId'));
      const payload = {
        full_name: convertForm.full_name.trim(),
        email: convertForm.email.trim() || null,
        phone: convertForm.phone.trim() || null,
        role_id: Number(convertForm.role_id),
        password: convertForm.password.trim(),
        date_of_birth: convertForm.date_of_birth || null,
        basic_salary: convertForm.basic_salary ? Number(convertForm.basic_salary) : null,
        allowances: convertForm.allowances ? Number(convertForm.allowances) : 0,
        deductions: convertForm.deductions ? Number(convertForm.deductions) : 0,
        clinic_ids: Number.isFinite(currentClinicId) && currentClinicId > 0 ? [currentClinicId] : []
      };

      await staffApi.create(payload);
      await jobInquiriesApi.updateStatus(convertInquiry.id, 'hired');
      toast.success('Job inquiry converted to staff successfully');
      closeConvertModal();
      fetchInquiries();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to convert inquiry to staff');
    } finally {
      setConverting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-700',
      reviewed: 'bg-yellow-100 text-yellow-700',
      shortlisted: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      hired: 'bg-purple-100 text-purple-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const hasActiveFilters = filters.search || filters.status || filters.position_type || filters.date_from || filters.date_to;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="font-display text-xl font-bold text-gray-900">Job Inquiries</h3>
          <p className="text-gray-500 mt-1">Manage job applications and inquiries</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn ${showFilters ? 'btn-primary' : 'btn-secondary'} relative`}
          >
            <HiOutlineFilter className="w-5 h-5 mr-2" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-danger-500 rounded-full border-2 border-white"></span>
            )}
          </button>
          <button onClick={handleCreate} className="btn btn-primary">
            <HiOutlinePlus className="w-5 h-5 mr-2" />
            New Inquiry
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="card animate-slide-down overflow-hidden">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="relative sm:col-span-2 lg:col-span-1">
                <label className="block text-xs text-gray-500 mb-1.5">Search</label>
                <div className="relative">
                  <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none z-10" />
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    placeholder="Search by name, email, contact..."
                    className="input pl-10 w-full"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="input w-full"
                >
                  <option value="">All Status</option>
                  <option value="new">New</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="shortlisted">Shortlisted</option>
                  <option value="rejected">Rejected</option>
                  <option value="hired">Hired</option>
                </select>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Position Type</label>
                <select
                  value={filters.position_type}
                  onChange={(e) => handleFilterChange('position_type', e.target.value)}
                  className="input w-full"
                >
                  <option value="">All Types</option>
                  {positionTypes.map((item) => (
                    <option key={item.id} value={item.name}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-2 lg:col-span-1">
                <label className="block text-xs text-gray-500 mb-1.5">Date Range</label>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => handleFilterChange('date_from', e.target.value)}
                    className="input flex-1 min-w-0"
                  />
                  <span className="text-gray-400 text-sm font-medium flex-shrink-0">to</span>
                  <input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => handleFilterChange('date_to', e.target.value)}
                    className="input flex-1 min-w-0"
                    min={filters.date_from || undefined}
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setFilters({
                    search: '',
                    status: '',
                    position_type: '',
                    date_from: '',
                    date_to: '',
                    page: 1
                  });
                }}
                className="btn btn-secondary whitespace-nowrap"
              >
                Clear Filters
              </button>
              <button type="submit" className="btn btn-primary whitespace-nowrap">
                <HiOutlineFilter className="w-5 h-5 mr-2" />
                Apply Filters
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Inquiries Table */}
      <div className="card overflow-hidden p-0">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
          </div>
        ) : inquiries.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <HiOutlineDocumentText className="w-12 h-12 mx-auto text-gray-300 mb-2" />
            <p>No job inquiries found</p>
            <button onClick={handleCreate} className="btn btn-primary mt-4">
              Add your first inquiry
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="table-header">Name</th>
                  <th className="table-header">Contact</th>
                  <th className="table-header">Position</th>
                  <th className="table-header">Experience</th>
                  <th className="table-header">Salary</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Created</th>
                  <th className="table-header"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {inquiries.map((inquiry) => (
                  <tr key={inquiry.id} className="hover:bg-gray-50">
                    <td className="table-cell">
                      <div>
                        <p className="font-medium text-gray-900">{inquiry.full_name}</p>
                        {inquiry.email && (
                          <p className="text-sm text-gray-500">{inquiry.email}</p>
                        )}
                      </div>
                    </td>
                    <td className="table-cell">
                      <p className="text-sm text-gray-600">{inquiry.contact_number}</p>
                    </td>
                    <td className="table-cell">
                      <span className="capitalize">{inquiry.position_type}</span>
                    </td>
                    <td className="table-cell">
                      {inquiry.experience_years ? `${inquiry.experience_years} years` : '-'}
                    </td>
                    <td className="table-cell">
                      {inquiry.salary_requirements ? (
                        <span>{inquiry.salary_currency} {parseFloat(inquiry.salary_requirements).toLocaleString()}</span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="table-cell">
                      <select
                        value={inquiry.status}
                        onChange={(e) => handleStatusChange(inquiry.id, e.target.value)}
                        className={`text-sm border-0 bg-transparent focus:ring-0 cursor-pointer ${getStatusColor(inquiry.status)}`}
                      >
                        <option value="new">New</option>
                        <option value="reviewed">Reviewed</option>
                        <option value="shortlisted">Shortlisted</option>
                        <option value="rejected">Rejected</option>
                        <option value="hired">Hired</option>
                      </select>
                    </td>
                    <td className="table-cell text-gray-500 text-sm">
                      {formatDateTimeShortIST(inquiry.created_at)}
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-1">
                        {inquiry.resume_file_name && (
                          <button
                            onClick={() => handleDownloadResume(inquiry)}
                            className="p-2 hover:bg-blue-100 rounded-lg"
                            title="Download Resume"
                          >
                            <HiOutlineDocumentDownload className="w-4 h-4 text-blue-600" />
                          </button>
                        )}
                        <button
                          onClick={() => openConvertModal(inquiry)}
                          className="p-2 hover:bg-emerald-100 rounded-lg"
                          title="Convert to Staff"
                        >
                          <HiOutlineUserAdd className="w-4 h-4 text-emerald-600" />
                        </button>
                        <button
                          onClick={() => handleEdit(inquiry)}
                          className="p-2 hover:bg-gray-100 rounded-lg"
                          title="Edit"
                        >
                          <HiOutlinePencil className="w-4 h-4 text-gray-500" />
                        </button>
                        <button
                          onClick={() => handleDelete(inquiry)}
                          className="p-2 hover:bg-red-100 rounded-lg"
                          title="Delete"
                        >
                          <HiOutlineTrash className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total} results
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={pagination.page === 1}
                className="btn btn-secondary text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={pagination.page >= pagination.totalPages}
                className="btn btn-secondary text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Job Inquiry Form Modal */}
      {showForm && (
        <JobInquiryForm
          inquiry={selectedInquiry}
          onClose={() => {
            setShowForm(false);
            setSelectedInquiry(null);
          }}
          onSuccess={() => {
            fetchInquiries();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && inquiryToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Delete Job Inquiry</h3>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setInquiryToDelete(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete this job inquiry?
              </p>
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <p className="font-medium">{inquiryToDelete.full_name}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {inquiryToDelete.position_type} • {inquiryToDelete.contact_number}
                </p>
              </div>
              <p className="text-sm text-amber-600">
                ⚠️ This action cannot be undone. The resume file will also be deleted.
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setInquiryToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showConvertModal && convertInquiry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Convert Inquiry to Staff</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Mapped fields are prefilled. Fill remaining details manually.
                </p>
              </div>
              <button onClick={closeConvertModal} className="text-gray-400 hover:text-gray-600">
                <HiX className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleConvertToStaff} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Name (mapped)</label>
                  <input
                    type="text"
                    value={convertForm.full_name}
                    onChange={(e) => setConvertForm((p) => ({ ...p, full_name: e.target.value }))}
                    className="input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Phone (mapped)</label>
                  <input
                    type="text"
                    value={convertForm.phone}
                    onChange={(e) => setConvertForm((p) => ({ ...p, phone: e.target.value }))}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Email (mapped)</label>
                  <input
                    type="email"
                    value={convertForm.email}
                    onChange={(e) => setConvertForm((p) => ({ ...p, email: e.target.value }))}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Role *</label>
                  <select
                    value={convertForm.role_id}
                    onChange={(e) => setConvertForm((p) => ({ ...p, role_id: e.target.value }))}
                    className="input w-full"
                    required
                  >
                    <option value="">Select role</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Password *</label>
                  <input
                    type="password"
                    value={convertForm.password}
                    onChange={(e) => setConvertForm((p) => ({ ...p, password: e.target.value }))}
                    className="input w-full"
                    minLength={6}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Date of Birth</label>
                  <input
                    type="date"
                    value={convertForm.date_of_birth}
                    onChange={(e) => setConvertForm((p) => ({ ...p, date_of_birth: e.target.value }))}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Basic Salary</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={convertForm.basic_salary}
                    onChange={(e) => setConvertForm((p) => ({ ...p, basic_salary: e.target.value }))}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Allowances</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={convertForm.allowances}
                    onChange={(e) => setConvertForm((p) => ({ ...p, allowances: e.target.value }))}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Deductions</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={convertForm.deductions}
                    onChange={(e) => setConvertForm((p) => ({ ...p, deductions: e.target.value }))}
                    className="input w-full"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeConvertModal}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={converting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                  disabled={converting}
                >
                  {converting ? 'Converting...' : 'Convert to Staff'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobInquiries;

